import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import './SmartContentDisplay.scss';
import { Button } from '@act/storybook';

interface SmartContentDisplayProps {
  children: React.ReactNode;
  maxHeight?: number;
  expandText?: string;
  collapseText?: string;
  minDelta?: number;
}

export const SmartContentDisplay: React.FC<SmartContentDisplayProps> = ({
  children,
  maxHeight = 100,
  expandText = 'Show more',
  collapseText = 'Show less',
  minDelta = 100,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldTruncate, setShouldTruncate] = useState(false);
  const [_contentHeight, setContentHeight] = useState<number>(0);
  const [currentHeight, setCurrentHeight] = useState<number>(maxHeight);
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Debounced resize handler to prevent rapid state updates
  const handleResize = useCallback(() => {
    if (contentRef.current && !isAnimating) {
      const height = contentRef.current.scrollHeight;
      const needsTruncation = height > maxHeight + minDelta;

      setContentHeight(height);
      setShouldTruncate(needsTruncation);

      if (!isExpanded) {
        setCurrentHeight(maxHeight);
      }
    }
  }, [maxHeight, minDelta, isExpanded, isAnimating]);

  // Initial content measurement and setup
  useLayoutEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      const needsTruncation = height > maxHeight + minDelta;

      setContentHeight(height);
      setShouldTruncate(needsTruncation);

      if (!isExpanded) {
        setCurrentHeight(maxHeight);
      }
    }
  }, [children, maxHeight, minDelta, isExpanded]); // Added isExpanded to dependencies

  // Handle resize events with debouncing
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const debouncedHandleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100); // 100ms debounce
    };

    // Use ResizeObserver for more reliable container size detection
    let resizeObserver: ResizeObserver;
    if (contentRef.current) {
      resizeObserver = new ResizeObserver(debouncedHandleResize);
      resizeObserver.observe(contentRef.current);
    }

    // Fallback to window resize for broader changes
    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [handleResize]);

  // Handle expansion/collapse height changes
  useLayoutEffect(() => {
    if (isExpanded && contentRef.current) {
      // When expanding, use the actual content height
      const actualHeight = contentRef.current.scrollHeight;
      setCurrentHeight(actualHeight);
    } else if (!isExpanded) {
      // When collapsing, use the max height
      setCurrentHeight(maxHeight);
    }
  }, [isExpanded, maxHeight]); // Removed contentHeight dependency to prevent loops

  const handleToggle = () => {
    if (isExpanded) {
      // Check if the top of the container is visible in the viewport
      if (contentRef.current) {
        const rect = contentRef.current.getBoundingClientRect();
        const isTopVisible = rect.top >= 0; // Top is visible if it's at or above viewport top

        // Only scroll if the top isn't visible
        if (!isTopVisible) {
          contentRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    }

    setIsAnimating(true);
    setIsExpanded(!isExpanded);

    // Reset animation flag after animation completes
    setTimeout(() => {
      setIsAnimating(false);
    }, 600); // Match the updated CSS transition duration
  };

  if (!shouldTruncate) {
    // Content is short enough to show completely without any truncation
    return (
      <div className="smart-content-container">
        <div ref={contentRef}>{children}</div>
      </div>
    );
  }

  return (
    <div className="smart-content-container">
      <div
        ref={contentRef}
        className={`smart-content-content ${isExpanded ? 'expanded' : ''} ${
          !isExpanded ? 'fade-enabled' : ''
        }`}
        style={{
          height: currentHeight,
        }}
      >
        {children}
      </div>

      <div
        className="smart-content-gradient-overlay"
        style={{
          opacity: isExpanded ? 0 : 1,
          pointerEvents: isExpanded ? 'none' : 'auto',
        }}
      ></div>
      <Button trackingName="smart-content-learn-more-button"
        variant="text"
        size="small"
        onClick={handleToggle}
        className={`smart-content-expand-button ${
          isExpanded ? 'expanded' : ''
        }`}
      >
        {isExpanded ? collapseText : expandText}
      </Button>
    </div>
  );
};
